import { useState } from "react";
import type { InningScorecard, IBowlingScoreCardItem } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";
import { phasesFor } from "../result/derive";
import type { ProgressionInning } from "../result/derive";
import CurrentBatters from "./CurrentBatters";
import { creaseBatters } from "./creaseBatters";
import { econOf, enumLabel, fmtDate, fullName, inningOrdinal, isMultiDayFormat, teamByName } from "./helpers";
import type { MatchInfo } from "./helpers";
import OverChart from "./OverChart";
import type { ChartKind } from "./OverChart";
import { Avatar, Crest, Empty, PlayerLink } from "./parts";
import PitchMap from "./PitchMap";
import { pitchRecorded } from "./pitchData";
import type { LiveBall, LiveStats, TeamForm } from "./useLiveStats";
import type { MatchProgression } from "../result/derive";

const SER = ["#2a78d6", "#eb6834"];
const SECS = [["all", "All"], ["score", "Scoring breakdown"], ["players", "Current players"], ["pship", "Partnerships"], ["man", "Manhattan"], ["rr", "Run rate graph"], ["worm", "Worm"], ["recent", "Recent matches"]] as const;
type Sec = (typeof SECS)[number][0];

type Partnership = {
  batsmanOne?: { playerId?: string; playerFirstName?: string | null; playerLastName?: string | null } | null;
  batsmanTwo?: { playerId?: string; playerFirstName?: string | null; playerLastName?: string | null } | null;
  totalRuns?: number | null; totalBalls?: number | null; batsmanOneRuns?: number | null; batsmanOneBalls?: number | null; batsmanTwoRuns?: number | null; batsmanTwoBalls?: number | null;
};

type Props = {
  match: MatchInfo;
  innings: InningScorecard[];
  liveIndex: number;
  isLive: boolean;
  stats?: LiveStats;
  statsLoading: boolean;
  statsError: boolean;
  progression?: MatchProgression;
  progressionLoading: boolean;
  curBowler?: IBowlingScoreCardItem;
  prevBowler?: IBowlingScoreCardItem;
};

const ballsOf = (stats: LiveStats | undefined, inningId?: string): LiveBall[] => stats?.innings.find((i) => i.inningId === inningId)?.balls ?? [];

/** Runs and wickets scored in overs (a, b] from the per-over progression, or null if the innings never reached over a. */
const phase = (p: ProgressionInning | undefined, a: number, b: number) => {
  if (!p) return null;
  const upto = (n: number) => {
    if (n <= 0) return { runs: 0, wkts: 0 };
    const o = [...p.overs].reverse().find((x) => x.over <= n);
    return o ? { runs: o.cumRuns, wkts: o.cumWickets } : null;
  };
  if (!p.overs.some((o) => o.over > a)) return null;
  const s = upto(a), e = upto(b);
  return s && e ? { runs: e.runs - s.runs, wkts: e.wkts - s.wkts } : null;
};

const hi = (a: number | null, b: number | null, dir: 1 | -1): [string, string] => {
  if (a === null && b === null) return ["na", "na"];
  if (a === null) return ["na", ""];
  if (b === null) return ["", "na"];
  if (a === b) return ["", ""];
  return (dir > 0 ? a > b : a < b) ? ["hi", ""] : ["", "hi"];
};

const Legend = ({ match, innings }: { match: MatchInfo; innings: InningScorecard[] }) => (
  <div className="lsleg">
    {innings.slice(0, 2).map((i, k) => (
      <span key={i.inningId}>
        <i style={{ background: SER[k] }} />
        {teamByName(match, i.battingTeamName).name || i.battingTeamName}
      </span>
    ))}
  </div>
);

const Scoring = ({ match, innings, stats, progression, isLive }: Pick<Props, "match" | "innings" | "stats" | "progression" | "isLive">) => {
  const [a, b] = innings;
  if (!a || !b) return <Empty>The comparison appears once the second innings starts.</Empty>;
  const pr = (i: InningScorecard) => progression?.innings.find((x) => x.inningId === i.inningId);
  const phases = phasesFor(match.matchType, a.maxOvers ?? 0, isMultiDayFormat(match));
  const rows: { l: string; s?: string; a: string; b: string; c: [string, string]; gap?: boolean }[] = [];
  phases.forEach((p) => {
    const x = phase(pr(a), p.from, p.to), y = phase(pr(b), p.from, p.to);
    rows.push({ l: p.short, s: `Overs ${p.from + 1} to ${p.to}`, a: x ? `${x.runs}/${x.wkts}` : "–", b: y ? `${y.runs}/${y.wkts}` : "–", c: hi(x?.runs ?? null, y?.runs ?? null, 1) });
  });
  const st = (i: InningScorecard) => {
    const bat = i.battingScoreCardResponseList ?? [];
    const f = bat.reduce((s, x) => s + (x.fours ?? 0), 0), sx = bat.reduce((s, x) => s + (x.six ?? 0), 0);
    const balls = ballsOf(stats, i.inningId).filter((x) => x.legal);
    const dot = balls.length ? Math.round((balls.filter((x) => x.runs === 0 && x.extraRuns === 0).length / balls.length) * 100) : null;
    const ex = (i.wide ?? 0) + (i.noBall ?? 0) + (i.bye ?? 0) + (i.legBye ?? 0) + (i.penalty ?? 0);
    return { f, s: sx, bd: f * 4 + sx * 6, dot, ex };
  };
  const A = st(a), B = st(b);
  ([["Sixes", "s", 1, true], ["Fours", "f", 1], ["Runs in boundaries", "bd", 1], ["Dot balls", "dot", -1, true, "%"], ["Runs from extras", "ex", 1]] as const).forEach(([l, k, d, gap, suf]) => {
    const x = A[k], y = B[k];
    rows.push({ l, a: x === null ? "–" : `${x}${suf ?? ""}`, b: y === null ? "–" : `${y}${suf ?? ""}`, c: hi(x, y, d), gap: !!gap });
  });
  const ta = teamByName(match, a.battingTeamName), tb = teamByName(match, b.battingTeamName);
  return (
    <div className="box">
      <div className="box-h">
        <h3>Scoring breakdown</h3>
        <span className="sub">The higher of the two is highlighted. For dot balls, the lower.</span>
      </div>
      <div className="sbh">
        <span><Crest team={ta} size={26} />{ta.name}</span>
        <span>{tb.name}<Crest team={tb} size={26} /></span>
      </div>
      <div className="sbt">
        {rows.map((r, i) => (
          <div key={r.l} className={`sbr ${r.gap || i === phases.length ? "gap" : ""}`}>
            <span className="l"><span className={`lsv ${r.c[0]}`}>{r.a}</span></span>
            <span className="m">{r.l}{r.s ? <small>{r.s}</small> : null}</span>
            <span className="r"><span className={`lsv ${r.c[1]}`}>{r.b}</span></span>
          </div>
        ))}
      </div>
      {isLive ? <div className="sbnote">{tb.name} are still batting, so their numbers grow ball by ball.</div> : null}
    </div>
  );
};

const Players = ({ match, innings, liveIndex, isLive, stats, statsLoading, statsError, curBowler, prevBowler }: Pick<Props, "match" | "innings" | "liveIndex" | "isLive" | "stats" | "statsLoading" | "statsError" | "curBowler" | "prevBowler">) => {
  const live = innings[liveIndex];
  const balls = ballsOf(stats, live?.inningId);
  const bowlers = [curBowler, prevBowler].filter((x): x is IBowlingScoreCardItem => !!x);
  const recorded = pitchRecorded(balls);
  return (
    <>
      <div className="box">
        <div className="box-h">
          <h3>Current batters</h3>
          <span className="sub">Every scoring shot this innings. Filter by runs or by bowler.</span>
        </div>
        <CurrentBatters batters={creaseBatters(live)} balls={balls} live={isLive} loading={statsLoading} error={statsError} />
      </div>
      <div className="box">
        <div className="box-h">
          <h3>Current bowlers</h3>
          <span className="sub">Where each ball pitched, to right- and left-hand batters</span>
        </div>
        {!bowlers.length ? (
          <Empty>No bowlers yet.</Empty>
        ) : (
          <div className="bwg">
            {bowlers.map((b, i) => {
              const name = fullName(b.playerFirstName, b.playerLastName);
              return (
                <div className="bwc" key={b.playerId}>
                  <div className="cb-h">
                    <Avatar name={name} team={live?.bowlingTeamName ?? match.teamOne?.teamName} />
                    <div style={{ minWidth: 0 }}>
                      <div className="cb-n">
                        <PlayerLink id={b.playerId} name={name} />
                        <span className={`crole ${i === 0 ? "b" : "n"}`}>{i === 0 ? "BOWLING" : "LAST OVER"}</span>
                      </div>
                      <div className="bwf">
                        <span>O</span><span>M</span><span>R</span><span>W</span><span>ECON</span>
                        <b>{b.over}</b><b>{b.maiden}</b><b>{b.runs}</b><b>{b.wickets ?? 0}</b><b>{econOf(b)}</b>
                      </div>
                    </div>
                  </div>
                  <div className="in">
                    {statsLoading && !balls.length ? <Empty>Loading…</Empty> : <PitchMap balls={balls.filter((x) => x.bowlerId === b.playerId)} recorded={recorded} />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

const Partnerships = ({ match, innings }: Pick<Props, "match" | "innings">) => (
  <div className="box">
    <div className="box-h">
      <h3>Partnerships</h3>
      <span className="sub">Each bar is split by what the two batters scored</span>
    </div>
    <div className="psg">
      {innings.slice(0, 2).map((inn, k) => {
        const list = ((inn as unknown as { partnershipAnalysisResponse?: Partnership[] }).partnershipAnalysisResponse ?? []);
        const mx = Math.max(1, ...list.map((p) => p.totalRuns ?? 0));
        const team = teamByName(match, inn.battingTeamName);
        const live = k === innings.length - 1 && inn.inningStatus === "IN_PROGRESS";
        return (
          <div key={inn.inningId}>
            <div className="psh"><Crest team={team} size={24} />{team.name}<small>{inningOrdinal(k)}</small></div>
            {list.length ? list.map((p, j) => {
              const ra = p.batsmanOneRuns ?? 0, rb = p.batsmanTwoRuns ?? 0, sum = Math.max(1, ra + rb), w = ((p.totalRuns ?? 0) / mx) * 100;
              const now = live && j === list.length - 1;
              return (
                <div key={j} className={`psr ${now ? "now" : ""}`}>
                  <div className="a"><PlayerLink id={p.batsmanOne?.playerId} name={fullName(p.batsmanOne?.playerFirstName, p.batsmanOne?.playerLastName)} /><span>{ra} ({p.batsmanOneBalls ?? 0})</span></div>
                  <div className="c">
                    <b>{p.totalRuns ?? 0}{now ? "*" : ""}</b><small>({p.totalBalls ?? 0})</small>{now ? <span className="lvt">NOW</span> : null}
                    <div className="bar"><i style={{ width: `${(w * ra) / sum}%`, background: SER[k] }} /><i style={{ width: `${(w * rb) / sum}%`, background: k ? "#F6BFA6" : "#A9CBF2" }} /></div>
                  </div>
                  <div className="b"><PlayerLink id={p.batsmanTwo?.playerId} name={fullName(p.batsmanTwo?.playerFirstName, p.batsmanTwo?.playerLastName)} /><span>{rb} ({p.batsmanTwoBalls ?? 0})</span></div>
                </div>
              );
            }) : <Empty>No partnerships yet.</Empty>}
          </div>
        );
      })}
    </div>
  </div>
);

const CHARTS: Record<"man" | "rr" | "worm", { title: string; sub: string }> = {
  man: { title: "Manhattan", sub: "Runs in each over. Rings mark wickets." },
  rr: { title: "Run rate graph", sub: "Run rate at the end of each over. Rings mark overs with wickets." },
  worm: { title: "Worm", sub: "Total runs over by over, with the target to chase" },
};

const Chart = ({ kind, match, innings, progression, loading }: { kind: ChartKind } & Pick<Props, "match" | "innings" | "progression"> & { loading: boolean }) => {
  const series = innings.slice(0, 2).flatMap((i, k) => {
    const p = progression?.innings.find((x) => x.inningId === i.inningId);
    return p ? [{ name: teamByName(match, i.battingTeamName).name || i.battingTeamName, color: SER[k], inning: p }] : [];
  });
  const target = innings.length > 1 ? (innings[0].runs ?? 0) + 1 : undefined;
  return (
    <div className="box">
      <div className="box-h">
        <h3>{CHARTS[kind].title}</h3>
        <span className="sub">{CHARTS[kind].sub}</span>
        <Legend match={match} innings={innings} />
      </div>
      <div className="lsch">{series.length ? <OverChart kind={kind} series={series} target={kind === "worm" ? target : undefined} noOvers={innings[0]?.maxOvers ?? 20} /> : <Empty>{loading ? "Loading chart…" : "Over-by-over data will appear once the first over is bowled."}</Empty>}</div>
    </div>
  );
};

const Recent = ({ forms, loading }: { forms: TeamForm[]; loading: boolean }) => {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="box">
      <div className="box-h">
        <h3>Recent matches</h3>
        <span className="sub">Last 5 in this tournament · newest on the right</span>
      </div>
      {!forms.length ? <Empty>{loading ? "Loading…" : "No recent matches."}</Empty> : forms.map((f) => {
        const rs = [...f.results].reverse();
        const isOpen = open === f.teamId;
        return (
          <div key={f.teamId} className={`rmr ${isOpen ? "open" : ""}`}>
            <button type="button" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : f.teamId)}>
              {f.teamName}
              <span className="fms">
                {rs.length ? rs.map((r, i) => <span key={r.matchId} className={`fm ${r.outcome} ${i === rs.length - 1 ? "last" : ""}`}>{r.outcome}</span>) : <span className="sub">No finished matches yet</span>}
              </span>
              <svg width="14" height="14" viewBox="0 0 12 12" fill="none"><path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
            </button>
            <ul>
              {[...rs].reverse().map((r) => (
                <li key={r.matchId}>
                  <span className={`fm ${r.outcome}`}>{r.outcome}</span>
                  <span><b>v {r.opponentName ?? "Unknown"}</b><span className="m">{fmtDate(r.date) || enumLabel(r.outcome)}</span></span>
                  <span className="sc2" style={{ fontFamily: "var(--sans)", fontSize: 13, fontWeight: 700 }}>
                    {r.outcome === "W" ? "Won" : r.outcome === "L" ? "Lost" : r.outcome === "T" ? "Tied" : r.outcome === "D" ? "Drawn" : "No result"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

const LiveStatsTab = (p: Props) => {
  const [sel, setSel] = useState<Sec>("all");
  const show = (k: Sec) => sel === "all" || sel === k;
  if (!p.innings.length) return <Empty>Live stats will appear once the first ball is bowled.</Empty>;
  return (
    <>
      <div className="lschips" role="group" aria-label="Show">
        {SECS.map(([k, l]) => (
          <button key={k} type="button" aria-pressed={sel === k} onClick={() => setSel(k)}>
            {l}
          </button>
        ))}
      </div>
      <div className="lsbody">
        {show("score") ? <Scoring match={p.match} innings={p.innings} stats={p.stats} progression={p.progression} isLive={p.isLive} /> : null}
        {show("players") ? <Players {...p} /> : null}
        {show("pship") ? <Partnerships match={p.match} innings={p.innings} /> : null}
        {(["man", "rr", "worm"] as const).map((k) => (show(k) ? <Chart key={k} kind={k} match={p.match} innings={p.innings} progression={p.progression} loading={p.progressionLoading} /> : null))}
        {show("recent") ? <Recent forms={p.stats?.recentForm ?? []} loading={p.statsLoading} /> : null}
      </div>
    </>
  );
};

export default LiveStatsTab;
